// Access levels and permission flags for rundowns. Pure: no schema, no I/O.
//
// Object-const style, not TypeScript `enum`. Values are unchanged from the old
// enums, so stored data and wire payloads keep matching.

export const RundownAccess = {
  WRITE: 'write',
  WRITE_COLUMN: 'write-column',
  READ: 'read',
} as const

export type RundownAccess = (typeof RundownAccess)[keyof typeof RundownAccess]

/**
 * Primitive bitwise permission flags for rundown access control (powers of 2).
 * Check with {@link hasPermission}, or `permissions & RundownPermission.EDIT`.
 */
export const RundownPermission = {
  VIEW: 1, // 000001 - read-only access
  EDIT: 2, // 000010 - edit all content
  SHOWCALL: 4, // 000100 - run/control the show
  MANAGE: 8, // 001000 - manage settings, access, invites
  // 16 reserved for future common permission
  EDIT_PARTIAL: 32, // 100000 - edit assigned columns only (special case)
} as const

export type RundownPermission = (typeof RundownPermission)[keyof typeof RundownPermission]

/** Precomposed permission sets for common roles. Each combines the flags above. */
export const RundownPermissionSet = {
  VIEWER: 1, // 000001
  EDITOR: 3, // 000011
  SHOWCALLER: 7, // 000111
  ADMIN: 15, // 001111
  PARTIAL_EDITOR: 33, // 100001
} as const

export type RundownPermissionSet = (typeof RundownPermissionSet)[keyof typeof RundownPermissionSet]

/**
 * JWT token payload for rundown authentication
 * Contains permission flags for access control
 *
 * 1. Bitwise permission flags stored as a single number Use bitwise AND to check: (payload.permissions &
 *    RundownPermission.EDIT) !== 0 Assign using enum values: RundownPermissionSet.EDITOR or custom combinations
 * 2. Legacy support during transition
 */
export interface RundownTokenPayload {
  rundownId: string
  permissions: number /* 1 */
  access: RundownAccess /* 2 */
}

/** Whether `permissions` (a bitmask) includes `flag`. */
export function hasPermission(permissions: number, flag: RundownPermission): boolean {
  return (permissions & flag) !== 0
}

/**
 * Convert a permission bitmask to the legacy {@link RundownAccess} value that the
 * token still carries.
 */
export function permissionsToAccess(permissions: number): RundownAccess {
  // Check for full edit permission
  if (hasPermission(permissions, RundownPermission.EDIT)) return RundownAccess.WRITE

  // Check for partial edit permission
  if (hasPermission(permissions, RundownPermission.EDIT_PARTIAL)) return RundownAccess.WRITE_COLUMN

  // View, or no permissions at all: read-only (the no-permission case shouldn't happen in normal flow)
  return RundownAccess.READ
}
